import React, { useEffect, useRef } from "react";
import Image from "next/image";
import parse from "html-react-parser";

import { cn } from "@/lib/utils";

import useProductEvents from "@/hooks/eventing/useProductEvents";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import { getSearchBarProductEventFormat } from "./searchBarEventing";
import type { SearchBarProductListingContext } from "./searchBarEventing";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
  setCorrelationId,
} from "@/utils/tagular/helpers";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import { useInView } from "react-intersection-observer";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import useTranslations from "@/i18n/useTranslations";
import { useLocale } from "next-intl";
import { withLocalePrefix } from "@/i18n/withLocalePrefix";

export type { SearchBarProductListingContext };

export type ProductSearchListItemProps = {
  id: string;
  productName: string;
  productTitleHtml?: string;
  productCategory: string;
  partner: {
    key: string;
    fullName: string;
  };
  partnerLogoUrl: string;
  /** Raw product type slug/name for Tagular variant mapping (Algolia/CMS). */
  variant?: string;
  viewedSince?: number;
  timeToComplete?: number;
  href: string;
  index?: number;
  location?: string;
  /** Compact typography for autocomplete; partner logo stays 92×43 per spec */
  compact?: boolean;
  listingContext?: SearchBarProductListingContext;
  analyticsDurationDisplay?: string;
  analyticsLevel?: string;
  analyticsFlexibility?: string;
  analyticsSkillsDisplay?: string;
};

const ProductSearchListItem = ({
  id,
  productName,
  productTitleHtml,
  productCategory,
  partner,
  partnerLogoUrl,
  viewedSince,
  timeToComplete,
  href,
  variant,
  index = 0,
  location = "embedded-search",
  compact = false,
  listingContext = "default-state",
  analyticsDurationDisplay,
  analyticsLevel,
  analyticsFlexibility,
  analyticsSkillsDisplay,
}: ProductSearchListItemProps) => {
  const t = useTranslations("Course");
  const tSearch = useTranslations("Search");
  const locale = useLocale();
  const localizedHref = withLocalePrefix(href, locale);
  const hasViewedRef = useRef(false);
  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const nameForAnalytics =
    (productTitleHtml ? productTitleHtml.replace(/<[^>]*>/g, "").trim() : "") ||
    productName;

  const brand = partner.key
    ? getBrandStringFromPartnerKeys([partner.key])
    : partner.fullName;

  const durationForAnalytics =
    analyticsDurationDisplay ||
    (timeToComplete != null ? `${timeToComplete} weeks` : undefined);

  const tagularVariant = variant ? translateVariant(variant) : undefined;

  const { formatType, formatSubtype } =
    getSearchBarProductEventFormat(listingContext);

  const tagularProduct: ProductClicked["product"] = {
    brand,
    category: getTagularSubject(productCategory),
    location,
    formatType,
    formatSubtype,
    name: nameForAnalytics,
    position: index + 1,
    variant: tagularVariant,
    productId: id,
  };

  const customDimensions: ProductClicked["customDimensions"] =
    createCustomDimensions([
      ["duration", durationForAnalytics],
      ["level", analyticsLevel],
      ["flexibility", analyticsFlexibility],
      ["skill", analyticsSkillsDisplay],
    ]);

  const { productClicked, productViewed, correlationId } = useProductEvents({
    product: tagularProduct,
    customDimensions,
  });

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      productViewed();
    }
  }, [inView, productViewed]);

  const handleClick = () => {
    setCorrelationId(correlationId);
    productClicked({ outboundUrl: localizedHref });
  };

  return (
    <a
      className={cn("flex gap-4", compact && "gap-3 items-start")}
      ref={inViewRef}
      href={localizedHref}
      onClick={handleClick}
    >
      <Image
        width={92}
        height={43}
        aria-hidden="true"
        className={cn(
          "flex-shrink-0 bg-white border border-putty-400 rounded object-contain",
          compact
            ? "h-[43px] w-[92px] border-putty-300 p-0"
            : "h-10 w-20 border-putty-400 p-2",
        )}
        src={partnerLogoUrl}
        alt={`${partner.fullName} logo`}
      />
      <div className="min-w-0 flex-1">
        <span
          className={cn(
            "font-bold line-clamp-2",
            compact
              ? "font-sans text-[18px] font-bold leading-[1.4] text-primary-500 line-clamp-2"
              : "line-clamp-1",
          )}
        >
          {productTitleHtml ? parse(productTitleHtml) : productName}
        </span>
        <div className="flex flex-wrap gap-1">
          <span
            className={cn(
              "py-0.5",
              compact
                ? "font-sans text-sm font-normal leading-[1.4] text-primary-500/75"
                : "text-xs text-black",
            )}
          >
            {partner.fullName}{" "}
            {timeToComplete &&
              ` • ${t("weeksCount", { count: timeToComplete })}`}
            {viewedSince && (
              <>
                <span className={cn("text-xs", compact ? "" : "text-black")}>
                  {" "}
                  •{" "}
                </span>
                <span className="text-putty-600 text-xs">
                  {tSearch("minutesAgo", { count: viewedSince })}
                </span>
              </>
            )}
          </span>
        </div>
      </div>
    </a>
  );
};

export default ProductSearchListItem;
